import { useEffect, useState, type ReactNode } from 'react';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { X } from 'lucide-react';
import { ShiftList } from '@/components/common/ShiftList';
import type { EventShift } from '@/utils/eventShifts';

export interface ShiftPickerModalProps {
  isOpen: boolean;
  title: string;
  intro: ReactNode;
  options: EventShift[];
  /** Short line under a shift, keyed by id (e.g. an overlap warning). */
  notes: Record<string, string>;
  /** Several shifts at once (picking up) or exactly one (moving). */
  multiple?: boolean;
  emptyText: string;
  confirmLabel: (selectedCount: number) => string;
  submitting?: boolean;
  onClose: () => void;
  onConfirm: (shiftIds: string[]) => void;
}

/**
 * The volunteer's shift chooser, shared by "Change shift" and "Pick shifts". Full shifts are
 * shown but cannot be chosen. The database re-checks everything on confirm, so a list that
 * went stale while open is refused there rather than trusted here.
 */
export function ShiftPickerModal({
  isOpen,
  title,
  intro,
  options,
  notes,
  multiple = false,
  emptyText,
  confirmLabel,
  submitting = false,
  onClose,
  onConfirm,
}: ShiftPickerModalProps) {
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) setSelected([]);
  }, [isOpen]);

  if (!isOpen) return null;

  const toggle = (id: string) =>
    setSelected((current) => {
      if (current.includes(id)) return current.filter((x) => x !== id);
      return multiple ? [...current, id] : [id];
    });

  const canConfirm = selected.length > 0 && !submitting;

  return (
    <Flex
      position="fixed"
      inset="0"
      bg="rgba(0,0,0,0.45)"
      align="center"
      justify="center"
      zIndex={10000}
      px="4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <Box
        role="dialog"
        aria-modal="true"
        aria-label={title}
        bg="white"
        borderRadius="24px"
        w="full"
        maxW="440px"
        maxH="90vh"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        boxShadow="0 20px 40px rgba(0,0,0,0.15)"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex align="center" justify="space-between" px="5" py="4" borderBottom="1px solid" borderColor="volyx.mediumGray">
          <Text fontSize="lg" fontWeight="bold" color="volyx.text">
            {title}
          </Text>
          <chakra.button
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={submitting}
            borderRadius="md"
            p="1"
            border="none"
            bg="transparent"
            cursor="pointer"
            _hover={{ bg: 'volyx.lightGray' }}
          >
            <X size={22} color="#1F2932" />
          </chakra.button>
        </Flex>

        <Box px="5" py="4" overflowY="auto" flex="1">
          <Text as="div" fontSize="sm" color="volyx.textSecondary" mb="4">
            {intro}
          </Text>

          {options.length === 0 ? (
            <Text fontSize="sm" color="volyx.textSecondary">
              {emptyText}
            </Text>
          ) : (
            <ShiftList
              shifts={options}
              selectedIds={selected}
              onToggle={toggle}
              blockFull
              notes={notes}
              disabled={submitting}
            />
          )}
        </Box>

        <Flex gap="3" px="5" py="4" borderTop="1px solid" borderColor="volyx.mediumGray">
          <chakra.button
            type="button"
            flex="1"
            py="3"
            borderRadius="xl"
            fontWeight="semibold"
            border="2px solid"
            borderColor="volyx.mediumGray"
            bg="white"
            color="volyx.text"
            cursor="pointer"
            disabled={submitting}
            onClick={onClose}
          >
            Cancel
          </chakra.button>
          <chakra.button
            type="button"
            flex="1"
            py="3"
            borderRadius="xl"
            fontWeight="semibold"
            border="none"
            bg={canConfirm ? 'volyx.primary' : 'volyx.textSecondary'}
            color="white"
            cursor={canConfirm ? 'pointer' : 'not-allowed'}
            disabled={!canConfirm}
            onClick={() => canConfirm && onConfirm(selected)}
          >
            {submitting ? 'Saving…' : confirmLabel(selected.length)}
          </chakra.button>
        </Flex>
      </Box>
    </Flex>
  );
}
